import {
  all,
  takeLatest,
  delay,
  call,
  select,
  put,
  race,
  take,
  takeEvery,
} from 'redux-saga/effects';
import { PapiNetworkbar, PapiUser } from '@wix/da-papi-types';
import { withCursor, streamActions } from '../../Stream';
import { watchedGroupsStreamId, recommendedGroupsStreamId } from './constants';
import {
  fetchMoreWatchedGroups,
  fetchMoreRecommendedGroups,
  initialFetch,
  unrecommendGroup,
  togglePinGroup,
  toggleWatchGroup,
  initializeNetworkBar,
  markChannelAsRead,
  updateNetworkBarSection,
  setNetworkBarSection,
} from './actions';
import { normalize } from 'normalizr';
import { userListSchema, userSchema } from '../../redux/normalizr/schemas/user';
import { fetchGroups, doPinGroup, doUnpinGroup, doUnrecommendGroup, doToggleSection } from './xhr';
import { NetworkBarStreamTypes } from './types';
import { addNotification } from '../../Notifications/redux/actionCreators';
import { NotificationType } from '../../Notifications';
import { getIsNetworkBarInitialized } from './selectors';
import { getDataEntityById } from '../../redux/entities';
import { putEntities } from '../../redux/entities/actions';
import { groupLeaveSuccess, groupJoinSuccess } from '../../redux/groups/actions';
import { FetchType } from '../../Stream/base/types';

function* processStreams(
  initData: PapiNetworkbar | false,
  content_type?: 'watched_groups' | 'recommended_groups'
) {
  const fetchWatched = content_type !== 'recommended_groups';
  const fetchRecommended = content_type !== 'watched_groups';

  if (fetchWatched) {
    if (!initData || initData.watchedGroups?.errorCode) {
      yield put(
        withCursor.actions.fetchFailure({
          streamId: watchedGroupsStreamId,
          fetchType: FetchType.Next,
          errorMsg: 'Failed to fetch groups',
        })
      );
    } else {
      const { result: items, entities } = normalize(
        initData.watchedGroups?.groups ?? [],
        userListSchema
      );

      yield all([
        put(
          withCursor.actions.fetchSuccess({
            streamId: watchedGroupsStreamId,
            items,
            entities,
            hasMore: !!initData.watchedGroups?.hasMore,
            cursor: initData.watchedGroups?.cursor,
          })
        ),
        put(setNetworkBarSection('watched_groups', !!initData.watchedGroups?.isCollapsed)),
      ]);
    }
  }

  if (fetchRecommended) {
    if (!initData || initData.recommendedGroups?.errorCode) {
      yield put(
        withCursor.actions.fetchFailure({
          streamId: recommendedGroupsStreamId,
          fetchType: FetchType.Next,
          errorMsg: 'Failed to fetch recommended groups',
        })
      );
    } else {
      const { result: items, entities } = normalize(
        initData.recommendedGroups?.groups ?? [],
        userListSchema
      );

      yield all([
        put(
          withCursor.actions.fetchSuccess({
            streamId: recommendedGroupsStreamId,
            items,
            entities,
            hasMore: false, // ability to load more removed per product request
            cursor: initData.recommendedGroups?.cursor,
          })
        ),
        put(setNetworkBarSection('recommended_groups', !!initData.recommendedGroups?.isCollapsed)),
      ]);
    }
  }
}

function* loadNetworkbarData(
  content_type?: 'watched_groups' | 'recommended_groups',
  cursor?: string
) {
  const result: PapiNetworkbar | false = yield call(fetchGroups, content_type, cursor);

  return result;
}

function* resetStream(content_type?: NetworkBarStreamTypes) {
  // we are not going to fire actual stream fetch actions here
  // so we can combine all the fetches into one when possible
  if (!content_type || content_type === 'watched_groups') {
    yield put(
      withCursor.actions.initialize({
        streamId: watchedGroupsStreamId,
        itemsPerFetch: 50,
        hasMore: true,
        fetchNextCallback: fetchMoreWatchedGroups,
      })
    );
    yield put(streamActions.markAsFetching(watchedGroupsStreamId));
  }
  if (!content_type || content_type === 'recommended_groups') {
    yield put(
      withCursor.actions.initialize({
        streamId: recommendedGroupsStreamId,
        itemsPerFetch: 3,
        hasMore: true,
        fetchNextCallback: fetchMoreRecommendedGroups,
      })
    );
    yield put(streamActions.markAsFetching(recommendedGroupsStreamId));
  }
}

function* handleFetchMoreWatchedGroups({
  payload: { streamId },
}: ReturnType<typeof fetchMoreWatchedGroups>) {
  yield delay(200);

  const cursor = yield select(withCursor.selectors.getCursor, streamId);
  const groups = yield call(loadNetworkbarData, 'watched_groups', cursor);
  yield call(processStreams, groups, 'watched_groups');
}

function* handleFetchMoreRecommendedGroups({
  payload: { streamId },
}: ReturnType<typeof fetchMoreRecommendedGroups>) {
  yield delay(200);

  const cursor = yield select(withCursor.selectors.getCursor, streamId);
  const groups = yield call(loadNetworkbarData, 'recommended_groups', cursor);
  yield call(processStreams, groups, 'recommended_groups');
}

function* handleTogglePinGroup({
  payload: { groupId, isPinnedAfter },
}: ReturnType<typeof togglePinGroup>) {
  const result = yield call(isPinnedAfter ? doPinGroup : doUnpinGroup, groupId);

  if (!result || !result.success) {
    yield put(
      addNotification(NotificationType.Generic, {
        isError: true,
        messageLocaleKey: isPinnedAfter
          ? 'networkBar.errors.failedToPin'
          : 'networkBar.errors.failedToUnpin',
      })
    );
    return;
  }

  yield call(resetStream, 'watched_groups');
  const groups = yield call(loadNetworkbarData, 'watched_groups');
  yield call(processStreams, groups, 'watched_groups');
}

function* handleUnrecommendGroup({ payload: { groupId } }: ReturnType<typeof unrecommendGroup>) {
  const result = yield call(doUnrecommendGroup, groupId);

  if (!result || !result.success) {
    yield put(
      addNotification(NotificationType.Generic, {
        isError: true,
        messageLocaleKey: 'networkBar.errors.failedToUnrecommend',
      })
    );
    return;
  }

  yield call(resetStream, 'recommended_groups');
  const groups = yield call(loadNetworkbarData, 'recommended_groups');
  yield call(processStreams, groups, 'recommended_groups');
}

function* handleToggleWatchGroup({
  payload: { isWatchingAfter },
}: ReturnType<typeof toggleWatchGroup>) {
  // if watching, reset both streams, if unwatching, just the watched one is enough
  const contentType = isWatchingAfter ? undefined : 'watched_groups';
  yield call(resetStream, contentType);
  const groups = yield call(loadNetworkbarData, contentType);
  yield call(processStreams, groups, contentType);
}

function* handleGroupMembershipChange() {
  yield call(resetStream);
  const groups = yield call(loadNetworkbarData);
  yield call(processStreams, groups);
}

function* handleMarkChannelAsRead({ payload: { channel } }: ReturnType<typeof markChannelAsRead>) {
  // main navigation is handled by reducer, we take care of groups here
  const group: PapiUser | undefined = yield select(getDataEntityById, userSchema, channel);
  if (!group?.networkbar?.hasNewContent) {
    return;
  }

  yield put(
    putEntities({
      entities: {
        [userSchema.key]: {
          [group.userId]: {
            ...group,
            networkbar: { ...group.networkbar, hasNewContent: false },
          },
        },
      },
    })
  );
}

function* handleToggleNetworkBarSection({
  payload: { section, isFoldedNow },
}: ReturnType<typeof updateNetworkBarSection>) {
  // fire and forget, it's not critical
  yield call(doToggleSection, section, isFoldedNow);
}

export default function* saga() {
  const isInitialized = yield select(getIsNetworkBarInitialized);

  if (!isInitialized) {
    yield call(resetStream);

    // we either init on component mount or init action from ssr, whichever comes first
    const { needFetchFirst, alreadyFetched } = yield race({
      needFetchFirst: take(initialFetch),
      alreadyFetched: take(initializeNetworkBar),
    });

    if (needFetchFirst) {
      const initData = yield call(loadNetworkbarData);
      if (initData) {
        yield call(processStreams, initData);
      }
      yield put(initializeNetworkBar(initData || {})); // init menu in store
    }
    if (alreadyFetched) {
      yield call(processStreams, alreadyFetched.payload);
    }
  }

  yield all([
    takeLatest(fetchMoreWatchedGroups, handleFetchMoreWatchedGroups),
    takeLatest(fetchMoreRecommendedGroups, handleFetchMoreRecommendedGroups),
    takeLatest(togglePinGroup, handleTogglePinGroup),
    takeLatest(toggleWatchGroup, handleToggleWatchGroup),
    takeLatest(unrecommendGroup, handleUnrecommendGroup),
    takeEvery(markChannelAsRead, handleMarkChannelAsRead),
    takeLatest(updateNetworkBarSection, handleToggleNetworkBarSection),
    takeEvery([groupJoinSuccess, groupLeaveSuccess], handleGroupMembershipChange),
  ]);
}
